import * as React from 'react';
import { cn } from '@/lib/utils/cn';
import { guardTokenEdit, snapSelection, splitTokens } from './tokens';

/**
 * The edit box of a block (E14-design §5): a plain text area that grows
 * with its text, with every `{{token}}` drawn as a chip behind the text.
 * A token cannot be typed over: typing inside one lands after it, deleting
 * part of one deletes all of it, and the caret and the selection snap to
 * its edges, so it moves (cut and paste) and deletes as one unit. No
 * rich-text editor (ADR-025 decision 6).
 *
 * The chips are a mirror: a copy of the text in the same box metrics, under
 * a transparent text area, with each token marked. The mirror also sets
 * the height, so the box grows without measuring.
 */
export interface TokenTextareaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'value' | 'onChange' | 'defaultValue'
> {
  value: string;
  onChange: (value: string) => void;
}

const BOX = 'rounded-card border p-2.5 text-body leading-relaxed [overflow-wrap:anywhere]';

export const TokenTextarea = React.forwardRef<HTMLTextAreaElement, TokenTextareaProps>(
  function TokenTextarea({ value, onChange, onSelect, className, ...props }, forwarded) {
    const inner = React.useRef<HTMLTextAreaElement | null>(null);
    const caret = React.useRef<number | null>(null);
    const setRef = (el: HTMLTextAreaElement | null) => {
      inner.current = el;
      if (typeof forwarded === 'function') forwarded(el);
      else if (forwarded) forwarded.current = el;
    };

    // Opened to edit: the caret goes after the text, where a person adds to it.
    const startsFocused = Boolean(props.autoFocus);
    React.useEffect(() => {
      const el = inner.current;
      if (el && startsFocused) el.setSelectionRange(el.value.length, el.value.length);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // After an edit the guard changed, put the caret where the guard says.
    React.useLayoutEffect(() => {
      const el = inner.current;
      if (el && caret.current !== null) {
        el.setSelectionRange(caret.current, caret.current);
        caret.current = null;
      }
    });

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const guarded = guardTokenEdit(value, e.target.value);
      if (guarded.text !== e.target.value) caret.current = guarded.caret;
      onChange(guarded.text);
    };

    const handleSelect = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const el = e.currentTarget;
      const snapped = snapSelection(el.value, el.selectionStart, el.selectionEnd);
      if (snapped.start !== el.selectionStart || snapped.end !== el.selectionEnd) {
        el.setSelectionRange(snapped.start, snapped.end, el.selectionDirection ?? undefined);
      }
      onSelect?.(e);
    };

    return (
      <div className="grid rounded-card bg-paper-0">
        <div
          aria-hidden="true"
          data-testid="token-mirror"
          className={cn(
            BOX,
            'pointer-events-none col-start-1 row-start-1 whitespace-pre-wrap border-transparent text-transparent',
          )}
        >
          {splitTokens(value, {}).map((p, i) =>
            p.kind === 'text' ? (
              <React.Fragment key={i}>{p.text}</React.Fragment>
            ) : (
              <mark
                key={i}
                data-token={p.name}
                className="rounded-[3px] bg-paper-100 text-transparent shadow-[inset_0_0_0_1px_var(--ink-200)]"
              >
                {p.raw}
              </mark>
            ),
          )}
          {/* Keeps a last empty line its height. */}{' '}
        </div>
        <textarea
          ref={setRef}
          rows={1}
          value={value}
          onChange={handleChange}
          onSelect={handleSelect}
          className={cn(
            BOX,
            'col-start-1 row-start-1 resize-none overflow-hidden border-ink-200 bg-transparent text-ink-900',
            'focus:outline-none focus:ring-2 focus:ring-accent-amber-600',
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);
